import * as THREE from 'three';
import { G, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, cable, mark, plate, ram, rib, seam } from './mech-kit';
/* ============================================================
   TITAN-3 · 核心 CORE：桶型軀幹 + 極寬肩承座
   ============================================================

   核心比例（local y0 = 腰線）：
     肩承座 x ±1.22　胸寬 2.30　腰寬 1.24　胸腰比 **1.85**
     腰線→肩 0.96

   **胸腰比刻意比 LANCER-2 低**（1.85 對 2.6）。V 錐拉得越極端讀起來越
   「健美」，而大力士不是健美 —— 他的腰跟胸一樣厚，靠的是整體積不是對比。
   把腰收細會讓這台變成「比較大的 LANCER-2」，那就白做了。

   桶感靠三件事，缺一就會讀回箱子：
     ① 腹段與胸腔**幾乎等寬**，中間只用一條縫分開（不是收窄）
     ② 側腹各掛一片外突的護甲 —— 從正面看，最寬的地方在**腰不在肩**
     ③ 胸腔本體仍然比胸甲窄（`lancer-core.ts` 記過：反過來會連成一面牆）

   斜方肌是這台的招牌：厚、上揚、幾乎碰到頭 —— 「沒有脖子」比
   LANCER-2 更誇張一階。 */

export function buildTitanCore(shape, P?) {
  P = P || { glow: coreGlow(shape) };
  var g = new THREE.Group();
  mark(g, shape, ['core-crit', 'core-burn', 'core-std']);

  /* 腰關節：粗、短、被前後兩排液壓夾住 —— **看不穿**。
     LANCER-2 的腰是「行程」（扭腰輸出），這台的腰是「承重」。 */
  g.add(mesh(G.cyl, MAT_P.trim, [0.62, 0.46, 0.62], [0, 0.04, 0]));
  g.add(mesh(G.tor, MAT_P.trim, [0.88, 0.88, 0.26], [0, 0.22, 0], [Math.PI / 2, 0, 0]));
  g.add(ram([0.40, 0.06, 0.26], 0.46, 0.13, 0.11, [-0.14, 0, 0]));
  g.add(ram([-0.40, 0.06, 0.26], 0.46, 0.13, 0.11, [-0.14, 0, 0]));
  g.add(ram([0.34, 0.06, -0.28], 0.46, 0.12, 0.10, [0.16, 0, 0]));
  g.add(ram([-0.34, 0.06, -0.28], 0.46, 0.12, 0.10, [0.16, 0, 0]));
  g.add(cable([0.44, 0.20, -0.30], 0.34));

  // 腹段：與胸腔幾乎等寬，只用一條縫分開 —— 桶型的第一層
  g.add(bevel(MAT_P.dark, 1.24, 0.48, 1.06, [0, 0.38, 0]));
  g.add(plate(MAT_P.armor, 1.06, 0.42, 0.30, [0, 0.34, 0.50], 0.10));            // 腹甲（不收窄）
  g.add(seam(1.30, 0.06, 0.12, [0, 0.62, 0.48], 0.08));
  // 側腹護甲：從正面看最寬的地方在腰 —— 桶型的第二層
  g.add(plate(MAT_P.armor, 0.30, 0.62, 0.86, [0.72, 0.36, 0], 0, -0.16, -0.18));
  g.add(plate(MAT_P.armor, 0.30, 0.62, 0.86, [-0.72, 0.36, 0], 0, 0.16, 0.18));
  g.add(rib(MAT_P.armor, 0.10, 0.10, 0.90, [0.78, 0.62, 0], 0, -0.16));

  // 胸腔（比胸甲窄）＋ 闊背：往外撐而不是往內收
  g.add(bevel(MAT_P.dark, 1.62, 0.96, 1.22, [0, 1.18 - 0.30, 0]));
  g.add(plate(MAT_P.armor, 0.34, 1.00, 1.04, [0.92, 0.90, 0.02], 0, -0.30, -0.14));
  g.add(plate(MAT_P.armor, 0.34, 1.00, 1.04, [-0.92, 0.90, 0.02], 0, 0.30, 0.14));
  g.add(rib(MAT_P.armor, 0.10, 1.00, 0.12, [1.00, 0.92, 0.12], 0, -0.30, -0.14));
  g.add(rib(MAT_P.armor, 0.10, 1.00, 0.12, [-1.00, 0.92, 0.12], 0, 0.30, 0.14));

  // 肩承座（極寬）＋ 斜方肌（厚、上揚，把頭埋掉）
  g.add(bevel(MAT_P.dark, 0.60, 0.80, 0.86, [1.22, 0.96, 0]));
  g.add(bevel(MAT_P.dark, 0.60, 0.80, 0.86, [-1.22, 0.96, 0]));
  /* 斜方肌**不能做成一片大斜板**。第一輪它是 0.92×0.74 的板、roll 0.44，
     那個尺寸的上表面在俯視鏡頭下正對主光 —— 實拍出來左右兩片連同肩甲
     糊成一片白，頭夾在中間完全讀不到（`.claude/notes/shots/titan/` 前兩輪）。
     現在拆成「內側一小片 + 一條稜 + 一道縫」，上表面積砍掉約六成。 */
  g.add(plate(MAT_P.armor, 0.66, 0.32, 0.54, [0.60, 1.02, -0.04], 0, -0.14, -0.22));
  g.add(plate(MAT_P.armor, 0.66, 0.32, 0.54, [-0.60, 1.02, -0.04], 0, 0.14, 0.22));
  g.add(seam(0.70, 0.05, 0.10, [0.60, 1.16, 0.14], 0, -0.14, -0.22));
  g.add(seam(0.70, 0.05, 0.10, [-0.60, 1.16, 0.14], 0, 0.14, 0.22));
  g.add(rib(MAT_P.armor, 0.86, 0.10, 0.10, [0.72, 1.14, 0.04], 0, -0.14, -0.22));
  g.add(rib(MAT_P.armor, 0.86, 0.10, 0.10, [-0.72, 1.14, 0.04], 0, 0.14, 0.22));

  if (shape === 'core-crit') {
    /* 精準核心：胸口正中一根外露稜柱，兩側裝甲往內夾。
       與另外兩台同一個語意（把能量集中到一個點），造型跟著這台的比例放大。 */
    g.add(mesh(G.bev, P.glow, [0.44, 0.86, 0.44], [0, 0.98, 0.56]));
    g.add(plate(MAT_P.armor, 0.86, 0.94, 0.34, [0.66, 1.00, 0.52], -0.10, -0.34, -0.14));
    g.add(plate(MAT_P.armor, 0.86, 0.94, 0.34, [-0.66, 1.00, 0.52], -0.10, 0.34, 0.14));
    g.add(rib(MAT_P.armor, 0.12, 0.94, 0.16, [0.40, 1.02, 0.70], -0.10, -0.34, -0.14));
    g.add(rib(MAT_P.armor, 0.12, 0.94, 0.16, [-0.40, 1.02, 0.70], -0.10, 0.34, 0.14));
    g.add(plate(MAT_P.armor, 1.54, 0.56, 0.30, [0, 1.16, -0.60], 0.12));

  } else if (shape === 'core-burn') {
    /* 過載核心：胸口大面積發光 + 三片橫過整個背的散熱鰭。
       這台的背比另外兩台寬 —— 鰭跟著加寬，剪影上是一面「牆」。 */
    g.add(plate(MAT_P.armor, 1.06, 0.46, 0.36, [0.58, 1.14, 0.52], -0.28, -0.10, -0.14));
    g.add(plate(MAT_P.armor, 1.06, 0.46, 0.36, [-0.58, 1.14, 0.52], -0.28, 0.10, 0.14));
    g.add(mesh(G.box, P.glow, [1.56, 0.22, 0.12], [0, 0.94, 0.62]));
    g.add(mesh(G.box, P.glow, [1.10, 0.14, 0.10], [0, 0.70, 0.58]));
    g.add(plate(MAT_P.armor, 1.72, 0.14, 0.60, [0, 1.34, -0.54], 0.60));
    g.add(plate(MAT_P.armor, 1.64, 0.14, 0.60, [0, 1.02, -0.60], 0.60));
    g.add(plate(MAT_P.armor, 1.52, 0.14, 0.60, [0, 0.70, -0.66], 0.60));
    g.add(mesh(G.cyl, MAT_P.trim, [0.36, 0.96, 0.36], [0, 0.92, -0.38]));

  } else {  /* core-std：四片胸甲（上下束）+ 中央深縫 + 環形核心 */
    /* **一側一片大板是死法**（實拍第一輪：正面讀成一面 2.2 寬的門板，
       頭整個被吃掉）。改成每側兩片 —— 上束往外上翻、下束往下垂，
       中間再夾一道 trim 深縫。同樣的體積，多兩條邊、兩塊影子。
       上束頂只到 local 1.36，讓出頭部（頭頂 1.56）的空間。 */
    /* **胸甲的上緣必須低於頭的下緣。** 分離變因實拍（`iso-titan3-arm.png`：
       關掉整條右臂，那兩片大板還在）證實正面那兩片白板是胸甲上束，
       它們原本頂到 local 1.28、又比頭前突 0.30 —— 頭被夾在中間，
       連著三輪調頭部大小都沒用，因為擋住它的不是斜方肌。
       現在上束頂 1.12、頭下緣 1.26，中間留一條看得穿的橫縫。 */
    g.add(plate(MAT_P.armor, 0.72, 0.40, 0.38, [0.68, 0.94, 0.52], -0.14, -0.16, -0.20));
    g.add(plate(MAT_P.armor, 0.72, 0.40, 0.38, [-0.68, 0.94, 0.52], -0.14, 0.16, 0.20));
    g.add(plate(MAT_P.armor, 0.66, 0.30, 0.34, [0.62, 0.66, 0.54], 0.26, -0.14, -0.30));
    g.add(plate(MAT_P.armor, 0.66, 0.30, 0.34, [-0.62, 0.66, 0.54], 0.26, 0.14, 0.30));
    g.add(seam(0.08, 0.60, 0.14, [0, 0.90, 0.68]));                                 // 中央深縫
    g.add(rib(MAT_P.armor, 0.12, 0.56, 0.16, [0, 0.92, 0.60], -0.22));              // 龍骨（窄）
    /* 核心環收到 0.26/0.46：第五輪實拍它是 0.36/0.64，在這台的近距離鏡頭下
       變成胸口正中一顆探照燈，比頭還搶眼 —— 發光件的面積要讓給剪影。 */
    g.add(mesh(G.cyl, P.glow, [0.26, 0.16, 0.26], [0, 0.48, 0.64], [Math.PI / 2, 0, 0]));
    g.add(mesh(G.tor, MAT_P.trim, [0.46, 0.46, 0.30], [0, 0.48, 0.64]));
    g.add(plate(MAT_P.armor, 1.56, 0.58, 0.30, [0, 1.14, -0.58], 0.12));            // 背板上段
    g.add(plate(MAT_P.armor, 1.40, 0.46, 0.28, [0, 0.56, -0.62], 0.02));            // 下段
  }
  return g;
}
